import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Save } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { mailbox, mailErrorCode, mailErrorMessage, MAILBOX_BAD_PASSWORD } from "../../api/mailbox";
import type { MailboxStatus } from "../../api/types";

// 「我的帳號」頁(設定 → 個人資料)裡的「我的信箱」一塊(規格 §2.2、§2.6)。
//
// - 已啟用 / 未啟用;啟用與換密碼都是同一支 `POST /mailbox/enable`(後端先登入主機試過才存)。
// - 停用 = 停止同步 + 刪掉存著的密碼。**已備份的信會留著**,本人照樣看得到 —— 確認文字要講明,
//   不然「停用」聽起來像「把我的信刪掉」。
// - 簽名檔:純文字,寫信 / 回信 / 轉寄時自動帶進內文底部(可刪)。

export function MailboxSettings() {
  const toast = useToast();
  const [status, setStatus] = useState<MailboxStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [signature, setSignature] = useState("");
  const [savingSignature, setSavingSignature] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const signatureId = useId();

  const load = useCallback(async () => {
    try {
      const got = await mailbox.status();
      setStatus(got);
      setFailed(false);
      if (got.enabled) {
        const sig = await mailbox.signature().catch(() => null);
        if (sig && typeof sig.signature === "string") setSignature(sig.signature);
      }
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enabled = status?.enabled === true;

  const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingPassword) return;
    if (password === "") {
      setPasswordError("請輸入信箱密碼");
      return;
    }
    setSavingPassword(true);
    setPasswordError(null);
    try {
      const got = await mailbox.enable(password);
      setPassword("");
      toast.push(enabled ? "信箱密碼已更新" : "信箱已啟用", "success");
      setStatus(got);
      if (!enabled) void load();
    } catch (err) {
      setPasswordError(mailErrorCode(err) === MAILBOX_BAD_PASSWORD ? "密碼不對" : mailErrorMessage(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const disable = async () => {
    if (
      !window.confirm(
        "停用後系統不再同步你的信,也會刪掉存在系統裡的信箱密碼。已備份的信會留著,你照樣看得到。確定停用?",
      )
    ) {
      return;
    }
    setDisabling(true);
    try {
      await mailbox.disable();
      toast.push("信箱已停用,已備份的信還在", "success");
      void load();
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setDisabling(false);
    }
  };

  const saveSignature = async () => {
    if (savingSignature) return;
    setSavingSignature(true);
    try {
      const got = await mailbox.saveSignature(signature);
      if (typeof got?.signature === "string") setSignature(got.signature);
      toast.push("簽名已儲存", "success");
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setSavingSignature(false);
    }
  };

  return (
    <section
      aria-label="我的信箱"
      className="flex flex-col gap-3 border-t border-[var(--border)] pt-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">我的信箱</h3>
        {status ? enabled ? <Badge tone="ok">已啟用</Badge> : <Badge tone="neutral">未啟用</Badge> : null}
        {status?.auth_failed ? <Badge tone="danger">密碼被拒,請重新輸入</Badge> : null}
      </div>
      {failed ? (
        <p className="m-0 text-sm text-[var(--text-3)]">讀不到信箱狀態,請稍後再試。</p>
      ) : status === null ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : (
        <>
          <p className="m-0 text-sm text-[var(--text-2)]">
            帳號就是登入用的 Email({status.email})。
            {enabled ? (
              <>
                在<Link to="/mailbox">信箱</Link>收信、寫信;只有你自己看得到。
              </>
            ) : (
              "輸入信箱密碼就能在系統裡收信、寫信;只有你自己看得到。"
            )}
          </p>

          <form onSubmit={submitPassword} className="flex flex-wrap items-end gap-2">
            <div className="w-64 max-w-full">
              <Input
                label={enabled ? "新的信箱密碼" : "信箱密碼"}
                type="password"
                autoComplete="current-password"
                hint={enabled ? "在郵件主機上改過密碼時,在這裡換" : undefined}
                value={password}
                error={passwordError}
                errorRole="alert"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" variant={enabled ? "secondary" : "primary"} loading={savingPassword} icon={<KeyRound aria-hidden="true" size={16} />}>
              {enabled ? "換密碼" : "啟用"}
            </Button>
          </form>

          {enabled ? (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor={signatureId} className="text-sm font-medium text-[var(--text-2)]">
                  簽名檔
                </label>
                <textarea
                  id={signatureId}
                  rows={4}
                  maxLength={2000}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm text-[var(--text)]"
                />
                <span className="text-xs text-[var(--text-3)]">純文字。寫信、回信、轉寄時自動帶進內文底部,寄出前刪得掉。</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button loading={savingSignature} icon={<Save aria-hidden="true" size={16} />} onClick={() => void saveSignature()}>
                  儲存簽名
                </Button>
                <Button variant="danger" loading={disabling} onClick={() => void disable()}>
                  停用
                </Button>
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
